import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { XMarkIcon, ArrowsPointingOutIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

const NODE_CANVAS_MODE = () => 'replace';

// Node types
const TAG_TYPE = 'tag';
const BM_TYPE = 'bookmark';

// Colors — indigo for tags, emerald for bookmarks
const TAG_DEFAULT   = '#a5b4fc'; // indigo-300
const TAG_SELECTED  = '#4338ca'; // indigo-700
const TAG_NEIGHBOR  = '#818cf8'; // indigo-400
const BM_DEFAULT    = '#6ee7b7'; // emerald-300
const BM_SELECTED   = '#059669'; // emerald-600
const BM_NEIGHBOR   = '#34d399'; // emerald-400
const DIMMED_COLOR  = '#e5e7eb'; // gray-200

function getDomain(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); }
  catch { return ''; }
}

const BipartiteGraph = ({ bookmarks, onNavigate, isDark = false }) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [containerWidth, setContainerWidth] = useState(600);
  const containerRef = useRef(null);
  const graphRef = useRef(null);
  const engineSettledRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // Build heterogeneous graph: tag nodes + bookmark nodes, edges bookmark→tag
  const graphData = useMemo(() => {
    if (!bookmarks.length) return { nodes: [], links: [] };

    // Count tags across all bookmarks
    const tagCounts = new Map();
    bookmarks.forEach(b => {
      (b.tags || []).forEach(t => tagCounts.set(t, (tagCounts.get(t) || 0) + 1));
    });

    const tagNodes = Array.from(tagCounts.entries()).map(([name, count]) => ({
      id: `tag:${name}`,
      label: name,
      type: TAG_TYPE,
      count,
    }));

    const bmNodes = bookmarks.map(b => ({
      id: `bm:${b.bookmarkId}`,
      title: b.title || getDomain(b.url) || b.bookmarkId,
      url: b.url || '',
      domain: getDomain(b.url || ''),
      tags: b.tags || [],
      type: BM_TYPE,
    }));

    // One edge per bookmark–tag pair
    const links = [];
    bookmarks.forEach(b => {
      (b.tags || []).forEach(t => {
        if (tagCounts.has(t)) {
          links.push({ source: `bm:${b.bookmarkId}`, target: `tag:${t}` });
        }
      });
    });

    return { nodes: [...tagNodes, ...bmNodes], links };
  }, [bookmarks]);

  // Bookmarks with no tags have no edges — keep them separate
  const { connectedGraphData, isolatedNodes } = useMemo(() => {
    const connectedIds = new Set(graphData.links.flatMap(l => [
      typeof l.source === 'object' ? l.source.id : l.source,
      typeof l.target === 'object' ? l.target.id : l.target,
    ]));
    return {
      connectedGraphData: {
        nodes: graphData.nodes.filter(n => connectedIds.has(n.id)),
        links: graphData.links,
      },
      isolatedNodes: graphData.nodes.filter(n => n.type === BM_TYPE && !connectedIds.has(n.id)),
    };
  }, [graphData]);

  const { neighborSet, neighborLinks } = useMemo(() => {
    if (!selectedNode) return { neighborSet: new Set(), neighborLinks: new Set() };
    const nSet = new Set();
    const lSet = new Set();
    graphData.links.forEach(link => {
      const srcId = typeof link.source === 'object' ? link.source.id : link.source;
      const tgtId = typeof link.target === 'object' ? link.target.id : link.target;
      if (srcId === selectedNode.id) { nSet.add(tgtId); lSet.add(link); }
      if (tgtId === selectedNode.id) { nSet.add(srcId); lSet.add(link); }
    });
    return { neighborSet: nSet, neighborLinks: lSet };
  }, [selectedNode, graphData.links]);

  // Neighbor nodes for the info panel
  const neighborNodes = useMemo(() => {
    if (!selectedNode) return [];
    return Array.from(neighborSet)
      .map(id => graphData.nodes.find(n => n.id === id))
      .filter(Boolean)
      .sort((a, b) => (b.count || 0) - (a.count || 0));
  }, [selectedNode, neighborSet, graphData.nodes]);

  // ── Visual accessors ────────────────────────────────────────────────────────

  const getNodeVal = useCallback(node =>
    node.type === TAG_TYPE ? Math.max(10, node.count * 4) : 6
  , []);

  const getNodeColor = useCallback(node => {
    const isTag = node.type === TAG_TYPE;
    if (!selectedNode) return isTag ? TAG_DEFAULT : BM_DEFAULT;
    if (node.id === selectedNode.id) return isTag ? TAG_SELECTED : BM_SELECTED;
    if (neighborSet.has(node.id)) return isTag ? TAG_NEIGHBOR : BM_NEIGHBOR;
    return DIMMED_COLOR;
  }, [selectedNode, neighborSet]);

  const getLinkColor = useCallback(link => {
    if (!selectedNode) return '#ddd6fe'; // violet-200
    if (neighborLinks.has(link)) return '#818cf8';
    return '#f3f4f6';
  }, [selectedNode, neighborLinks]);

  const drawNode = useCallback((node, ctx, globalScale) => {
    const color = getNodeColor(node);

    if (node.type === TAG_TYPE) {
      // Tags: larger circles, label always visible
      const radius = Math.max(8, node.count * 4);
      ctx.beginPath();
      ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
      ctx.fillStyle = color;
      ctx.fill();

      const isDimmed = color === DIMMED_COLOR;
      const fontSize = Math.max(8, 12 / globalScale);
      ctx.font = `bold ${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = isDimmed ? '#9ca3af' : (color === TAG_SELECTED ? '#fff' : (isDark ? '#c7d2fe' : '#312e81'));
      ctx.fillText(node.label, node.x, node.y);
    } else {
      // Bookmarks: small circles, label on zoom or selection
      const radius = 5;
      ctx.beginPath();
      ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
      ctx.fillStyle = color;
      ctx.fill();

      const isSelected = selectedNode && node.id === selectedNode.id;
      if (globalScale > 2.5 || isSelected) {
        const label = node.title.length > 28 ? node.title.slice(0, 26) + '\u2026' : node.title;
        const fontSize = Math.max(7, 9 / globalScale);
        ctx.font = `${fontSize}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillStyle = isDark ? '#e2e8f0' : '#1e293b';
        ctx.fillText(label, node.x, node.y + radius + 2);
      }
    }
  }, [getNodeColor, selectedNode, isDark]);

  // ── Interaction handlers ────────────────────────────────────────────────────

  const handleNodeClick = useCallback(node => {
    setSelectedNode(prev => prev?.id === node.id ? null : node);
  }, []);

  const handleBackgroundClick = useCallback(() => setSelectedNode(null), []);

  const handleEngineStop = useCallback(() => {
    engineSettledRef.current = true;
    graphRef.current?.zoomToFit(400, 40);
  }, []);

  const handleFitView = useCallback(() => {
    if (!graphRef.current) return;
    graphRef.current.zoom(1, 0);
    graphRef.current.centerAt(0, 0, 0);
    setTimeout(() => graphRef.current?.zoomToFit(400, 40), 50);
  }, []);

  const handleReheat = useCallback(() => {
    if (!graphRef.current) return;
    engineSettledRef.current = false;
    graphRef.current.d3ReheatSimulation();
  }, []);

  // Configure D3 forces: tags act as hubs, bookmarks orbit them
  useEffect(() => {
    const g = graphRef.current;
    if (!g) return;
    // Moderate repulsion — tags are large so need some room, but links keep bookmarks close
    g.d3Force('charge')?.strength(-120);
    // Short link distance so bookmarks cluster tightly around their tag hubs
    g.d3Force('link')?.distance(50).strength(0.8);
    g.d3ReheatSimulation();
  }, [connectedGraphData]);

  // ── Edge cases ──────────────────────────────────────────────────────────────

  if (graphData.nodes.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center text-gray-500 dark:text-gray-400">
        No bookmarks to display.
      </div>
    );
  }

  if (connectedGraphData.nodes.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center text-gray-500 dark:text-gray-400">
        <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">No connections yet.</p>
        <p className="text-sm">Add tags to your bookmarks to see the graph.</p>
      </div>
    );
  }

  const tagCount = connectedGraphData.nodes.filter(n => n.type === TAG_TYPE).length;
  const bmCount = connectedGraphData.nodes.filter(n => n.type === BM_TYPE).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4 md:items-start">

        {/* Legend + stats panel */}
        <div className="w-full md:w-44 md:flex-shrink-0 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Legend</p>

          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2">
              <span className="h-4 w-4 rounded-full flex-shrink-0" style={{ backgroundColor: TAG_DEFAULT }} />
              <span className="text-xs text-gray-600 dark:text-gray-300">Tag <span className="text-gray-400 dark:text-gray-500">({tagCount})</span></span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: BM_DEFAULT }} />
              <span className="text-xs text-gray-600 dark:text-gray-300">Bookmark <span className="text-gray-400 dark:text-gray-500">({bmCount})</span></span>
            </div>
          </div>

          <p className="text-xs text-gray-400 dark:text-gray-500 pt-3 border-t border-gray-100 dark:border-gray-700 leading-relaxed">
            Tag size = bookmark count<br />
            Edges = tag membership
          </p>

          {isolatedNodes.length > 0 && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
              {isolatedNodes.length} bookmark{isolatedNodes.length !== 1 ? 's' : ''} have no tags
            </p>
          )}
        </div>

        {/* Graph + controls */}
        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex justify-end gap-2">
            <button
              onClick={handleReheat}
              className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-sm transition-colors"
              title="Re-run layout simulation"
            >
              Re-layout
            </button>
            <button
              onClick={handleFitView}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-50 border border-gray-200 rounded-md shadow-sm transition-colors"
              title="Fit all nodes into view"
            >
              <ArrowsPointingOutIcon className="h-3.5 w-3.5" />
              Fit to view
            </button>
          </div>

          <div ref={containerRef} className="bg-white dark:bg-gray-900 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700">
            <ForceGraph2D
              ref={graphRef}
              graphData={connectedGraphData}
              nodeVal={getNodeVal}
              nodeColor={getNodeColor}
              linkColor={getLinkColor}
              onNodeClick={handleNodeClick}
              onBackgroundClick={handleBackgroundClick}
              nodeCanvasObject={drawNode}
              nodeCanvasObjectMode={NODE_CANVAS_MODE}
              onEngineStop={handleEngineStop}
              enableNodeDrag={true}
              warmupTicks={0}
              cooldownTime={5000}
              d3AlphaMin={0.001}
              minZoom={0.05}
              maxZoom={8}
              width={containerWidth}
              height={520}
              d3VelocityDecay={0.25}
              backgroundColor={isDark ? '#111827' : '#ffffff'}
            />
          </div>

          {/* Info panel */}
          {selectedNode && (
            <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-4 ${
              selectedNode.type === TAG_TYPE ? 'border-indigo-200 dark:border-indigo-800' : 'border-emerald-200 dark:border-emerald-800'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">

                  {selectedNode.type === TAG_TYPE ? (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-300 px-3 py-1 rounded-full text-sm font-medium">
                          {selectedNode.label}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {selectedNode.count} bookmark{selectedNode.count !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {neighborNodes.length > 0 && (
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Bookmarks with this tag:</p>
                          <div className="space-y-1 max-h-40 overflow-y-auto">
                            {neighborNodes.map(node => (
                              <button
                                key={node.id}
                                onClick={() => setSelectedNode(node)}
                                className="w-full text-left flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 py-0.5"
                              >
                                <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: BM_DEFAULT }} />
                                <span className="truncate">{node.title}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      <button
                        onClick={() => onNavigate(selectedNode.label)}
                        className="mt-3 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium transition-colors"
                      >
                        View all bookmarks →
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">{selectedNode.title}</p>
                      {selectedNode.domain && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{selectedNode.domain}</p>
                      )}

                      {neighborNodes.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Tags:</p>
                          <div className="flex flex-wrap gap-1">
                            {neighborNodes.map(node => (
                              <button
                                key={node.id}
                                onClick={() => setSelectedNode(node)}
                                className="text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 px-2 py-0.5 rounded-full transition-colors"
                              >
                                {node.label}
                                <span className="text-indigo-400 dark:text-indigo-500 ml-1">({node.count})</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedNode.url && (
                        <a
                          href={selectedNode.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 inline-flex items-center gap-1 text-sm text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 font-medium transition-colors"
                        >
                          Open <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </>
                  )}
                </div>

                <button
                  onClick={() => setSelectedNode(null)}
                  className="ml-3 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
                  aria-label="Dismiss"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isolatedNodes.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">
            Bookmarks with no tags
          </p>
          <div className="flex flex-wrap gap-2">
            {isolatedNodes.map(node => (
              <a
                key={node.id}
                href={node.url}
                target="_blank"
                rel="noopener noreferrer"
                title={node.title}
                className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 hover:text-emerald-700 dark:hover:text-emerald-400 px-3 py-1 rounded-full text-sm transition-colors max-w-xs truncate"
              >
                {node.domain || node.title}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BipartiteGraph;
